import { PlusOutlined } from "@ant-design/icons";
import { Button, Select } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import {
	useCreateDepartment,
	useDepartmentsList,
	useMoveDepartment,
	useUpdateDepartment,
} from "../hooks/useDepartments";
import type { Department, DepartmentDetail, DepartmentFormValues } from "../types";
import { DepartmentFormModal } from "./DepartmentFormModal";
import { DepartmentsExplorerTable } from "./DepartmentsExplorerTable";
import { MoveDepartmentModal } from "./MoveDepartmentModal";

/**
 * Bo'lim profilining "Bo'linmalar" tabi — shu bo'limning BEVOSITA farzandlari.
 *
 * Drill-down shu yerda davom etadi: `DepartmentsExplorerTable` qatori
 * `/departments/$id?tab=bolinmalar` ga o'tadi (`rowSearch`) va YANGI sahifa
 * o'sha farzandning O'Z "Bo'linmalar" tabi bilan ochiladi — alohida holat
 * saqlanmaydi. `?tab=` siz `resolveTab` "umumiy" ga qaytib, har bosqichda
 * foydalanuvchi tabni qayta bosishi kerak bo'lardi.
 *
 * Yangi bo'linma HAR DOIM shu bo'lim ostida yaratiladi: formadagi
 * "Ota bo'lim" ko'rinadi, lekin QULFLANGAN (`parentLocked`).
 *
 * ⚠️ Tahrirlash/Ko'chirish `DepartmentsPage` bilan AYNI naqshda ulangan:
 * jadval `departments:update` bilan ikkala tugmani chizadi va `onEdit` /
 * `onMove` uzatilmasa ular JIMGINA hech narsa qilmasdi (`onEdit?.(dept)`).
 *
 * ⚠️⚠️ TO'LIQ `department` obyekti oladi, faqat `departmentId` EMAS —
 * «Yangi bo'linma» tugmasi `department.isActive` ni ko'rishi kerak
 * (arxivlangan bo'limga farzand qo'shib bo'lmaydi, backend 409
 * `departments.parentArchived`). `DepartmentDetailPage` da bu obyekt
 * allaqachon keshda, ikkinchi so'rov keraksiz.
 */
export function DepartmentSubunitsTab({ department }: { department: DepartmentDetail }) {
	const { t } = useTranslation("departments");
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<Department | null>(null);
	const [moveTarget, setMoveTarget] = useState<Department | null>(null);
	// Ildiz sahifasidagidek (`DepartmentsPage`) standart — Faol.
	const [isActive, setIsActive] = useState(true);

	// Forma va Ko'chirish modallari `DepartmentTreeSelect` uchun to'liq,
	// sahifalanmagan ro'yxatga tayanadi (`DepartmentsPage` bilan bir xil).
	const { data: departments = [] } = useDepartmentsList();

	const createDepartment = useCreateDepartment();
	const updateDepartment = useUpdateDepartment();
	const moveDepartment = useMoveDepartment();

	const closeForm = () => {
		setFormOpen(false);
		setEditing(null);
	};

	const handleSubmit = (values: DepartmentFormValues) => {
		if (editing) {
			// Ota-onani shu forma orqali o'zgartirib bo'lmaydi — buning uchun
			// alohida "Ko'chirish" modali bor.
			updateDepartment.mutate(
				{
					id: editing.id,
					input: { name: values.name, gradeId: values.gradeId, code: values.code },
				},
				{ onSuccess: closeForm }
			);
			return;
		}

		// Qattiq `parentId: department.id` — halol, chunki formadagi "Ota
		// bo'lim" QULFLANGAN (`parentLocked`): operator uni na almashtira, na
		// tozalay oladi, ya'ni ko'rinadigan qiymat va yuboriladigan qiymat bir xil.
		createDepartment.mutate({ ...values, parentId: department.id }, { onSuccess: closeForm });
	};

	return (
		<div>
			<div className="mb-4 flex justify-between items-center gap-3 flex-wrap">
				{/*
				 * `allowClear` YO'Q — backend'da «hammasi» qiymati yo'q
				 * (`isActive` standarti `true`), tozalash «filtr yo'q» degan
				 * yolg'on taassurot berardi (`DepartmentsPage` bilan bir xil).
				 */}
				<Select
					value={isActive}
					onChange={setIsActive}
					options={[
						{ value: true, label: t("page.statusActive") },
						{ value: false, label: t("page.statusArchived") },
					]}
					style={{ width: 160 }}
				/>
				{/* ⚠️ Arxivlangan bo'limga farzand qo'shib bo'lmaydi (backend 409
				 * `departments.parentArchived`) — tugma UMUMAN chizilmaydi. */}
				{department.isActive ? (
					<PermissionGate resource="departments" action="create">
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setEditing(null);
								setFormOpen(true);
							}}
						>
							{t("detail.addChildButton")}
						</Button>
					</PermissionGate>
				) : null}
			</div>

			{/*
			 * ⚠️ `key` MAJBURIY: drill-down `/departments/$id` dan `/departments/$id`
			 * ga — AYNI marshrut, faqat parametr o'zgaradi, va router komponentni
			 * qayta mount QILMAYDI. `usePaginatedQuery` sahifani O'Z `useState`
			 * ida saqlaydi — usiz ota bo'limning 2-sahifasidan farzandga
			 * o'tilganda farzandning 2-sahifasi so'ralib, BO'SH jadval chiqardi
			 * (`DepartmentsPage` dagi `key` bilan bir xil tuzoq). Holat filtri
			 * o'zgarganda ham xuddi shunday — kalitga `isActive` QO'SHILADI, aks
			 * holda filtr almashtirilganda eski sahifa raqami saqlanib, bo'sh
			 * jadval chiqardi. `?tab=bolinmalar` drill-down'da SAQLANGANI uchun
			 * `Tabs` ning `destroyOnHidden` i ham remount bermaydi — `key`
			 * yagona himoya.
			 */}
			<DepartmentsExplorerTable
				key={`${department.id}|${isActive}`}
				parentId={department.id}
				isActive={isActive}
				rowSearch={{ tab: "bolinmalar" }}
				onEdit={(dept) => {
					setEditing(dept);
					setFormOpen(true);
				}}
				onMove={(dept) => setMoveTarget(dept)}
			/>

			<DepartmentFormModal
				open={formOpen}
				editing={editing}
				departments={departments}
				defaultParentId={department.id}
				parentLocked
				isSubmitting={createDepartment.isPending || updateDepartment.isPending}
				onCancel={closeForm}
				onSubmit={handleSubmit}
			/>

			<MoveDepartmentModal
				open={moveTarget !== null}
				department={moveTarget}
				departments={departments}
				isSubmitting={moveDepartment.isPending}
				onCancel={() => setMoveTarget(null)}
				onSubmit={(parentId) => {
					if (moveTarget) {
						moveDepartment.mutate(
							{ id: moveTarget.id, parentId },
							{ onSuccess: () => setMoveTarget(null) }
						);
					}
				}}
			/>
		</div>
	);
}
